import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FileOutlined, ReloadOutlined, LinkOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ProForm, ProFormText, ProFormTextArea, ModalForm } from '@ant-design/pro-components';

interface Annex {
  id: number;
  title: string;
  content: string;
  file_url: string;
  status_flag: number;
  create_time: string;
}

export default function Annex() {
  const [annexes, setAnnexes] = useState<Annex[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAnnex, setEditingAnnex] = useState<Annex | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = ProForm.useForm();

  useEffect(() => {
    fetchAnnexes();
  }, []);

  const fetchAnnexes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/annex', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setAnnexes(resp.data || []);
      } else {
        message.error(resp?.error || '获取附件列表失败');
      }
    } catch (err) {
      message.error('获取附件列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingAnnex(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Annex) => {
    setEditingAnnex(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/annex/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchAnnexes();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingAnnex 
        ? `/api/admin/annex/${editingAnnex.id}`
        : '/api/admin/annex';
      const method = editingAnnex ? 'PUT' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingAnnex ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchAnnexes();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  // 过滤数据
  const filteredAnnexes = annexes.filter(a => 
    a.title?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.content?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const columns: ColumnsType<Annex> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id) => <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>#{id}</span>
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '内容描述',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (text) => <span style={{ color: '#6b7280' }}>{text}</span>
    },
    {
      title: '文件地址',
      dataIndex: 'file_url',
      key: 'file_url',
      ellipsis: true,
      render: (url: string) => (
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ 
            color: '#3b82f6',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}
        >
          <LinkOutlined />
          {url}
        </a>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{text}</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#3b82f6' }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个附件吗？"
            description="删除后将无法恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      {/* 页面头部 */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon" style={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)' }}>
            <FileOutlined />
          </div>
          <div>
            <h2>附件管理</h2>
            <p>管理系统附件文件</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索附件..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchAnnexes}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)'
            }}
          >
            新增附件
          </Button>
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#06b6d4', fontWeight: 600 }}>{filteredAnnexes.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredAnnexes}
          loading={loading}
          rowKey="id"
          className="admin-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      {/* 编辑弹窗 */}
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FileOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingAnnex ? '编辑附件' : '新增附件'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={600}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="title"
            label="标题"
            placeholder="请输入附件标题"
            rules={[{ required: true, message: '请输入标题' }]}
          />
          <ProFormTextArea
            name="content"
            label="内容描述"
            placeholder="请输入内容描述"
            fieldProps={{ rows: 4 }}
          />
          <ProFormText
            name="file_url"
            label="文件地址"
            placeholder="请输入文件地址"
            rules={[{ required: true, message: '请输入文件地址' }]}
          />
        </div>
      </ModalForm>
    </div>
  );
}
